var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import styles from '@cof/fs-icon-core/lib/icon.lit.min.js';
import { css, html, LitElement, svg } from 'lit';
import { property } from 'lit/decorators.js';
import { unsafeSVG } from 'lit/directives/unsafe-svg.js';
const iconMap = new Map();
/**
 * The web component implementation of the Financial Services icon component. This package, together with data from `@cof/fs-icons` will enable developers to import Gravity-approved, optimized icons into their application and use them within the confines of the design system.
 *
 * ## Installation
 *
 * ```bash
 * npm i @cof/fs-icons @cof/fs-icon-wc
 * ```
 *
 * ## Usage
 *
 * To import the side-effectful registration of `<fs-icon>`, call
 *
 * ```javascript
 * import { FsIcon} from '@cof/fs-icon-wc/fs-icon.js';
 * ```
 *
 * To import the `FsIcon` class alone either to use as a base class or self-register the component or to gain access to the `add` static method, use
 *
 * ```javascript
 * import { FsIcon} from '@cof/fs-icon-wc';
 * ```
 *
 * To import icons to be used in the component, you'll need to grab those icons from the `@cof/fs-icons` package. The names and exports for those icons should match the data in the Figma file you are referencing. For example, if you are looking to use the icon `fs-16-checkmark`, you would do the following:
 *
 * ```javascript
 * import { FsIcon } from '@cof/fs-icon-wc/fs-icon.js';
 * import { fs16Checkmark } from '@cof/fs-icons';
 *
 * FsIcon.add(fs16Checkmark); // this can take either single icons or an array of icons
 * ```
 *
 * You can then use the corresponding icon in your HTML:
 *
 * ```html
 * <fs-icon icon="fs-16-checkmark" description="Checkmark icon"></fs-icon>
 * ```
 *
 * Note, the imports from `@cof/fs-icons` will be pascalCase where as the icon names for the element will be kebab-case.
 *
 * @element fs-icon
 */
export class FsIcon extends LitElement {
    constructor() {
        super(...arguments);
        this._canonicalName = 'icon';
        /** Placeholder for interactive */
        this._interactive = false;
        /** The icon's description, should reflect the usage and content of the icon */
        this.description = '';
        /** If set to true, the icon will become disabled */
        this.disabled = false;
        /** The icon name to use, this key must be added to the icon's constructor via the static add method */
        this.icon = '';
        /**
         * The icon's variant. Possible values are 'primary', 'secondary', 'inverse',
         * 'feedback-info', 'feedback-warning', 'feedback-positive' and 'feedback-critical'.
         * The variant, and the icon's interactive state, determine the item's fill within the
         * confines of the design system.
         */
        this.variant = 'neutral';
    }
    static get styles() {
        return [
            styles,
            css `
        :host { box-sizing: border-box; color: inherit; display: inline-block; line-height: 0; }
        :host(:focus) {
          box-shadow: none;
          outline: 0;
        }
      `
        ];
    }
    /**
     * This method takes a tuple of [iconName, iconData] from the @cof/fs-icons package.
     * You can pass in either a single icon definition or a list of icon definitions that will
     * become renderable once registered.
     *
     * @example
     * ```javascript
     * import { FsIcon } from '@cof/fs-icon-wc/fs-icon';
     * import { fs24Placeholder } from '@cof/fs-icons';
     *
     * FsIcon.add(fs24Placeholder);
     * ```
     */
    static add(iconDefinition) {
        if (!Array.isArray(iconDefinition) || !iconDefinition.length) {
            throw new Error('You must pass a valid icon definition to FsIcon.add');
        }
        if (Array.isArray(iconDefinition[0])) {
            iconDefinition.forEach((definition) => {
                iconMap.set(definition[0], definition[1]);
            });
        }
        else {
            const definition = iconDefinition;
            iconMap.set(definition[0], definition[1]);
        }
    }
    /** This item takes an icon name and returns whether the component has that icon registered */
    static has(key) {
        return iconMap.has(key);
    }
    /** This method will return a list of registered icon names */
    static get list() {
        return Array.from(iconMap.keys());
    }
    /** When set to true the icon can serve as a button with primary, secondary and inverse variants */
    get interactive() {
        return this._interactive;
    }
    set interactive(interactive) {
        this._interactive = interactive;
        const event = new CustomEvent('interactive-toggle', { detail: interactive, bubbles: true });
        this.dispatchEvent(event);
        this.requestUpdate();
    }
    /** Manages the icon's role attribute based on accessibility rules */
    get role() {
        if (!this.description) {
            return 'presentation';
        }
        else {
            return this.interactive ? 'button' : 'img';
        }
    }
    /** If the icon used is one of the 16-pixel versions this will be small */
    get size() {
        const size = this.icon && this.icon.includes('16') ? 'small' : 'normal';
        /** Only toggle the attribute on the browser */
        this.toggleAttribute?.('compact', size === 'small');
        return size;
    }
    _getSVGIcon(icon) {
        const svgPath = iconMap.get(icon);
        if (svgPath) {
            return unsafeSVG(iconMap.get(icon));
        }
        else {
            // TODO: create a logging package
            console.error(`fs-${this._canonicalName}: ${icon} cannot be found, please check this icon is registered and spelled correctly. Not all icons have both sizes (16, 24)
\nExample:
\nimport { FsIcon } from "@cof/fs-icon-wc/fs-icon.js";
import { fs16Placeholder, fs24Placeholder } from '@cof/fs-icons';
\nFsIcon.add([fs16Placeholder, fs24Placeholder]); \n `);
            return '';
        }
    }
    firstUpdated() {
        const event = new CustomEvent('interactive-toggle', { detail: this.interactive, bubbles: true });
        this.dispatchEvent(event);
    }
    render() {
        if (this.role === 'button') {
            return html `<button 
        class="fs-icon-button ${this.size === 'small' ? 'fs-icon__button--compact' : ''}" 
        ?disabled="${this.disabled}">
        <svg 
          viewBox="${this.size === 'small' ? '0 0 16 16' : '0 0 24 24'}" 
          class="fs-icon fs-icon--interactive 
          ${this.disabled ? 'fs-icon--disabled' : ''} 
          ${this.size === 'small' ? 'fs-icon--small' : ''} 
          fs-icon--${this.variant}" 
          xmlns="http://www.w3.org/2000/svg" 
          part="fs-icon" 
          aria-label="${this.description}">
          ${this._getSVGIcon(this.icon)}
        </svg>
      </button>`;
        }
        return svg `<svg 
      viewBox="${this.size === 'small' ? '0 0 16 16' : '0 0 24 24'}" 
      class="fs-icon ${this.interactive ? 'fs-icon--interactive' : ''} 
      ${this.disabled ? 'fs-icon--disabled' : ''}
      ${this.size === 'small' ? 'fs-icon--small' : ''}
      fs-icon--${this.variant}" 
      xmlns="http://www.w3.org/2000/svg" 
      part="fs-icon" 
      aria-label="${this.description}" 
      role="${this.role}" 
      aria-disabled="${this.disabled}">
      ${this._getSVGIcon(this.icon)}
    </svg>`;
    }
}
/** The element's canonical name for use with safe define */
FsIcon.canonicalName = 'icon';
/**
 * Override the default shadow root options to enable ShadowRoot.prototype.delegatesFocus
 */
FsIcon.shadowRootOptions = {
    ...LitElement.shadowRootOptions,
    delegatesFocus: true
};
__decorate([
    property({ type: String })
], FsIcon.prototype, "description", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], FsIcon.prototype, "disabled", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], FsIcon.prototype, "interactive", null);
__decorate([
    property({ type: String, reflect: true })
], FsIcon.prototype, "icon", void 0);
__decorate([
    property({ type: String, reflect: true })
], FsIcon.prototype, "variant", void 0);
//# sourceMappingURL=Icon.js.map